"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CaliforniaRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const unwrap_1 = require("../../unwrap");
const undeterminedStricterOfClock_1 = require("../undeterminedStricterOfClock");
const _stateRules_1 = require("./_stateRules");
/**
 * California (DTSC) accumulation limits, 22 CCR division 4.5 chapter 12.
 */
const CA_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR", // TODO - we should remove this, and remove it from the derivation cases below, but only after confirming that there are safeguards against it ever being saved to the db for CA
];
const CA_DERIVATIONS = {
    effectiveGeneratorStatus: {
        from: ["generatorStatus", "stateGeneratorStatus"],
        label: "Effective California tier",
        citation: "22 CCR 66262.13",
        description: (0, unwrap_1.unwrap) `A facility's California generator tier is the stricter of federal
      status and California's own determination.
      LQG on either -> LQG. That is the strictest possible so it doesn't matter if the
      other is unknown. Otherwise if either is unknown, we can't be sure it's not
      LQG, so just honestly report unknown.
      The tier is never VSQG. California's regulations mention VSQGs, but define
      SQG as generating "less than 2,200 lbs" per month with no lower bound, so a
      facility meeting the federal VSQG definition is also a California SQG and
      the SQG rules apply. DTSC does not recognize VSQG in practice.`,
        cases: [
            // LQG on either axis wins outright, even if the other is unknown.
            { when: { generatorStatus: "LQG" }, produces: "LQG" },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            // SQG or VSQG on both axes: CA recognizes no VSQG tier (see the
            // description), so these all land SQG.
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            // Insufficient information
            { when: { generatorStatus: "SQG", stateGeneratorStatus: null }, produces: null },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            { when: { generatorStatus: "VSQG", stateGeneratorStatus: null }, produces: null },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            { when: { generatorStatus: null, stateGeneratorStatus: null }, produces: null },
        ],
    },
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste"],
        label: "California Effective Subclassification",
        citation: "22 CCR 66261.9, 66273.1",
        description: (0, unwrap_1.unwrap) `Whether waste is universal waste here depends on whether California's own
      chapter 23 program covers the category — the \`wasteKind\` UNIVERSAL_WASTE categorization coming in is only
      whether the line might be UW somewhere. CA adds
      electronic waste, cathode ray tubes, and photovoltaic modules, and it declines federal
      pesticides. Whether CA covers a particular category is determined elsewhere and is taken as
      given here. A category chapter 23 does not cover is ordinary hazardous waste on the chapter
      12 limits.
      Used oil and pharmaceutical waste get ordinary chapter 12 treatment too: California
      regulates used oil as a non-RCRA state hazardous waste (HSC 25250.1), so part 279's absence
      of a generator storage limit never reaches a California container, and California never
      adopted 40 CFR 266 Subpart P, which leaves pharmaceutical waste fully regulated hazardous
      waste.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "RCRA",
            },
            {
                when: { wasteKind: "USED_OIL" },
                produces: "RCRA",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL" },
                produces: "RCRA",
            },
            {
                when: { wasteKind: "RCRA" },
                produces: "RCRA",
            },
            {
                when: { wasteKind: null },
                produces: "RCRA",
            },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
/**
 * Chapter 23's one-year limit, stated once for every universal waste handler —
 * California has no small/large handler split, so unlike the federal rows this
 * carries a single citation.
 */
const CA_UNIVERSAL_WASTE_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "22 CCR 66273.35(a)",
    jurisdiction: "CA",
};
const CA_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION ──────────────────────────────────────────────
    // Keyed on RCRA, which spans used oil, pharmaceutical waste, and
    // universal-waste categories chapter 23 does not cover — the kind
    // derivation is what says all of those live on chapter 12.
    {
        given: { effectiveWasteKind: "RCRA", effectiveGeneratorStatus: "LQG" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" },
        },
    },
    {
        given: { effectiveWasteKind: "RCRA", effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: false },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" },
        },
    },
    {
        given: { effectiveWasteKind: "RCRA", effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: true },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" },
        },
        note: "California grants the same over-200-mile extension federal law does.",
    },
    {
        given: { effectiveWasteKind: "RCRA", effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL" },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 22 CCR 66262.15 ──────────────────────────
    // Spans every status rather than keying on the tier derivation — see the
    // note.
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "22 CCR 66262.15(a)",
                jurisdiction: "CA",
            },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" },
        },
        note: (0, unwrap_1.unwrap) `
      The same numeric caps as the federal rule, but imposed by California's own regulation
      and reaching every generator tier — California never adopted the federal VSQG
      carve-out. California also adds a deadline federal law has no analog for: waste must
      move within one year of the container's accumulation start date, full or not.`,
    },
    // ── UNIVERSAL WASTE — 22 CCR division 4.5 chapter 23 ───────────────────
    // California runs its own universal waste program rather than adopting the
    // federal one, so these rows carry California citations end to end. Two
    // differences from the federal rows worth naming: California states the
    // one-year limit ONCE, in 66273.35(a), for every universal waste handler
    // (no small/large handler split, hence no dual citation), and the scope-out
    // is 66273.1(b) reading "an alternative set of management standards in lieu
    // of regulation as hazardous wastes pursuant to chapters 10 through 16" —
    // chapter 12 is inside that range, so 66262.15's satellite caps don't apply
    // and the caps are NO_LIMIT. Generator status does not factor in to UW handling.
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66273.35(a)", jurisdiction: "CA" },
        },
        note: (0, unwrap_1.unwrap) `
      One year from the date the universal waste was generated or received, for every
      universal waste handler — California does not split the limit by handler size the way
      40 CFR 273.15(a) and 273.35(a) do.`,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "22 CCR 66261.9, 66273.1(b)", jurisdiction: "CA" },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66273.35(a)", jurisdiction: "CA" },
        },
        note: (0, unwrap_1.unwrap) `
      66261.9 makes these wastes subject to chapter 23 instead, and 66273.1(b) supplies
      chapter 23's standards in lieu of chapters 10 through 16. Since chapter 12 is the source of
      CA's satellite caps and 3-day move window, those are not applied. The one-year
      limit runs the same in a satellite or central area.`,
    },
];
/**
 * The imperative twin of the `effectiveGeneratorStatus` derivation above.
 * Sharing `CaDeterminations` keeps the two from drifting on shape; the sweep
 * holds their contents to each other.
 */
function effectiveCaliforniaStatus(ctx) {
    if (ctx.generatorStatus === "LQG" || ctx.stateGeneratorStatus === "LARGE_QUANTITY_GENERATOR") {
        return "LQG";
    }
    if (ctx.generatorStatus === null || ctx.stateGeneratorStatus === null) {
        return null;
    }
    if (ctx.generatorStatus === "SQG" ||
        ctx.generatorStatus === "VSQG" ||
        ctx.stateGeneratorStatus === "SMALL_QUANTITY_GENERATOR" ||
        ctx.stateGeneratorStatus === "VERY_SMALL_QUANTITY_GENERATOR") {
        return "SQG";
    }
    // Exhaustiveness guard: only accepts `never`, so an unhandled status
    // combination fails to compile.
    return ((..._) => null)(ctx.generatorStatus, ctx.stateGeneratorStatus);
}
const CA_ACCUMULATION_THRESHOLD_NOTE = "California's own on-site quantity conditions are not modeled: the thresholds are the federal ones, " +
    "keyed on the federal status alone, spanning California's own generator status. Pending SME review of " +
    "22 CCR 66262.14 and 66262.16 for California-specific figures.";
/**
 * Spec, not snapshot. Restates the federal thresholds deliberately — see the note —
 * keyed on the raw federal axis rather than California's effective status,
 * because that is what the inherited slot reads.
 */
const CA_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
        note: CA_ACCUMULATION_THRESHOLD_NOTE,
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
        note: CA_ACCUMULATION_THRESHOLD_NOTE,
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
        note: CA_ACCUMULATION_THRESHOLD_NOTE,
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
        note: CA_ACCUMULATION_THRESHOLD_NOTE,
    },
];
const CA_SOURCES = [
    {
        title: "22 CCR division 4.5 Chapter 11 (§ 66261)",
        url: "https://www.law.cornell.edu/regulations/california/title-22/division-4.5/chapter-11",
    },
    {
        title: "22 CCR division 4.5 Chapter 12 (§ 66262)",
        url: "https://www.law.cornell.edu/regulations/california/title-22/division-4.5/chapter-12",
    },
    {
        title: "22 CCR division 4.5 Chapter 23 (§ 66273)",
        url: "https://www.law.cornell.edu/regulations/california/title-22/division-4.5/chapter-23",
    },
];
/** California (DTSC) accumulation limits, 22 CCR division 4.5 chapter 12. */
class CaliforniaRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.expectations = CA_EXPECTATIONS;
        this.accumulationThresholdExpectations = CA_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = CA_DERIVATIONS;
        this.sources = CA_SOURCES;
        this.stateStatusDomain = CA_STATE_STATUS_DOMAIN;
    }
    effectiveWasteKind(ctx) {
        const federalKind = super.effectiveWasteKind(ctx);
        switch (federalKind) {
            case "PHARMACEUTICAL":
            case "USED_OIL":
                return "RCRA";
            default:
                return federalKind;
        }
    }
    centralClock(ctx) {
        // Universal waste is regulated under chapter 23 in lieu of chapter 12
        // (66273.1(b)), so it never reaches the generator-tier clocks below. Used
        // oil and pharmaceutical waste DO fall through deliberately: California
        // regulates used oil as a non-RCRA state hazardous waste and never
        // adopted Subpart P, so both stay on the ordinary standards.
        if (this.effectiveWasteKind(ctx) === "UNIVERSAL_WASTE") {
            return CA_UNIVERSAL_WASTE_CLOCK;
        }
        const effective = effectiveCaliforniaStatus(ctx);
        if (effective === "LQG") {
            return { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" };
        }
        if (effective === "SQG") {
            if (ctx.mustTransport200Miles) {
                return { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" };
            }
            return { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" };
        }
        return (0, undeterminedStricterOfClock_1.undeterminedStricterOfClock)(ctx, "California");
    }
    satelliteCaps(ctx) {
        if (this.effectiveWasteKind(ctx) === "UNIVERSAL_WASTE") {
            /**
             * 66261.9 subjects these wastes to chapter 23, and 66273.1(b) supplies chapter
             * 23's standards "in lieu of regulation as hazardous wastes pursuant to
             * chapters 10 through 16" — chapter 12, where the satellite caps live, is
             * inside that range.
             */
            return {
                kind: "NO_LIMIT",
                citation: "22 CCR 66261.9, 66273.1(b)",
                jurisdiction: "CA",
            };
        }
        // Federal numbers under California's own regulation, with no VSQG
        // carve-out — CA never adopted that relief.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "22 CCR 66262.15(a)",
            jurisdiction: "CA",
        };
    }
    satelliteClock(ctx) {
        if (this.effectiveWasteKind(ctx) === "UNIVERSAL_WASTE") {
            // Same one year as a central area: chapter 23 measures from the date the
            // waste was generated or received, not from where it sits.
            return CA_UNIVERSAL_WASTE_CLOCK;
        }
        // "Full or one year, whichever comes first" — the caps cover full; this
        // is the year.
        return { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" };
    }
}
exports.CaliforniaRules = CaliforniaRules;
//# sourceMappingURL=CA.js.map